import { z } from "zod";
import { LANGUAGE_DISPLAY_NAMES } from "../constants/languages.js";
import { assertUnreachable } from "../helpers/utilities.js";
import {
  CANDIDATE_ALIGN_FIELD_NAME,
  CefrAssessmentFieldNameToLanguage,
  SpecialAssessmentFieldName
} from "./assessment.js";
import { SpecialProjectCustomFieldName } from "./project.js";
const CustomFieldType = {
  Text: "text",
  Date: "date",
  DateAndTime: "date_and_time",
  Number: "number",
  Boolean: "boolean",
  UserReference: "user_reference"
  // Reference to a user/teammate. The value is an ID and the display_value is email.
};
const allCustomFieldTypes = [
  CustomFieldType.Text,
  CustomFieldType.Date,
  CustomFieldType.DateAndTime,
  CustomFieldType.Number,
  CustomFieldType.Boolean,
  CustomFieldType.UserReference
];
const customFieldTypeSchema = z.enum(allCustomFieldTypes);
const dateCustomFieldTypes = [
  CustomFieldType.Date,
  CustomFieldType.DateAndTime
];
const dateCustomFieldTypeSchema = z.enum(dateCustomFieldTypes);
function normalizeIsoDateTimeOffset(value) {
  if (typeof value !== "string") {
    return value;
  }
  return value.replace(/([+-]\d{2})(\d{2})$/, "$1:$2");
}
const customFieldValueSchemaByType = {
  [CustomFieldType.Text]: z.string(),
  [CustomFieldType.Date]: z.iso.date(),
  [CustomFieldType.DateAndTime]: z.preprocess(
    normalizeIsoDateTimeOffset,
    z.iso.datetime({ offset: true })
  ),
  [CustomFieldType.Number]: z.number(),
  [CustomFieldType.Boolean]: z.boolean(),
  [CustomFieldType.UserReference]: z.string()
};
function createCustomFieldDataSchema(valueSchema) {
  return z.object({
    value: valueSchema.nullable(),
    display_value: z.string().optional()
  });
}
const customFieldDataSchemaByType = {
  [CustomFieldType.Text]: createCustomFieldDataSchema(
    customFieldValueSchemaByType[CustomFieldType.Text]
  ),
  [CustomFieldType.Date]: createCustomFieldDataSchema(
    customFieldValueSchemaByType[CustomFieldType.Date]
  ),
  [CustomFieldType.DateAndTime]: createCustomFieldDataSchema(
    customFieldValueSchemaByType[CustomFieldType.DateAndTime]
  ),
  [CustomFieldType.Number]: createCustomFieldDataSchema(
    customFieldValueSchemaByType[CustomFieldType.Number]
  ),
  [CustomFieldType.Boolean]: createCustomFieldDataSchema(
    customFieldValueSchemaByType[CustomFieldType.Boolean]
  ),
  [CustomFieldType.UserReference]: createCustomFieldDataSchema(
    customFieldValueSchemaByType[CustomFieldType.UserReference]
  )
};
const customFieldValueDataSchema = z.union([
  customFieldDataSchemaByType[CustomFieldType.Text],
  customFieldDataSchemaByType[CustomFieldType.Date],
  customFieldDataSchemaByType[CustomFieldType.DateAndTime],
  customFieldDataSchemaByType[CustomFieldType.Number],
  customFieldDataSchemaByType[CustomFieldType.Boolean],
  customFieldDataSchemaByType[CustomFieldType.UserReference]
]);
const incomingCustomFieldSchema = z.object({
  key: z.string(),
  value: z.union([z.string(), z.number(), z.boolean(), z.null()])
});
const SpecialInterviewCustomFieldName = {
  FountainApplicantId: "fountain_applicant_id"
};
const specialCustomFieldDetails = {
  [SpecialProjectCustomFieldName.AdvancedNextSteps]: {
    display_name: "Advancement message next steps",
    field_type: CustomFieldType.Text
  },
  [SpecialInterviewCustomFieldName.FountainApplicantId]: {
    display_name: "Fountain applicant ID",
    field_type: CustomFieldType.Text
  },
  [SpecialAssessmentFieldName.CefrLanguageLevelEnglish]: {
    display_name: "CEFR English level estimate",
    field_type: CustomFieldType.Text
  },
  [SpecialAssessmentFieldName.CefrLanguageLevelGerman]: {
    display_name: "CEFR German level estimate",
    field_type: CustomFieldType.Text
  }
};
const interviewSectionAssessmentCustomFieldNamePrefix = "interview_section_assessment";
const interviewSectionAssessmentCustomFieldDetails = {
  // TODO: Make this dynamic by requiring it to be passed into findOrCreateSpecialCustomFieldDefinition?
  display_name: "Interview section assessment",
  field_type: CustomFieldType.Text
};
function checkIsInterviewSectionAssessmentCustomFieldName(fieldName) {
  return fieldName !== void 0 && fieldName.startsWith(`${interviewSectionAssessmentCustomFieldNamePrefix}:`);
}
function buildInterviewSectionAssessmentCustomFieldName(interviewSectionDefinitionId) {
  return `${interviewSectionAssessmentCustomFieldNamePrefix}:${interviewSectionDefinitionId}`;
}
const fieldSalesTrainingAssessmentPillarCustomFieldNamePrefix = "field_sales_training_assessment:pillar";
function checkIsFieldSalesTrainingAssessmentPillarCustomFieldName(fieldName) {
  return fieldName !== void 0 && fieldName.startsWith(
    `${fieldSalesTrainingAssessmentPillarCustomFieldNamePrefix}:`
  );
}
function findSpecialCustomFieldDetails(fieldName) {
  if (checkIsInterviewSectionAssessmentCustomFieldName(fieldName)) {
    return interviewSectionAssessmentCustomFieldDetails;
  }
  if (checkIsFieldSalesTrainingAssessmentPillarCustomFieldName(fieldName)) {
    return {
      // TODO: Make this dynamic by requiring it to be passed into findOrCreateSpecialCustomFieldDefinition?
      display_name: "Field sales training pillar score",
      field_type: CustomFieldType.Number
    };
  }
  return specialCustomFieldDetails[fieldName];
}
function getCustomFieldDisplayName(customField) {
  if (customField.field_name in CefrAssessmentFieldNameToLanguage) {
    const languageName = LANGUAGE_DISPLAY_NAMES[CefrAssessmentFieldNameToLanguage[customField.field_name]];
    return `CEFR ${languageName} Level Assessment`;
  }
  if (customField.display_name === CANDIDATE_ALIGN_FIELD_NAME) {
    return "Candidate/Job Alignment";
  }
  return customField.display_name;
}
const CustomFieldProvider = {
  /** Non-integration system-managed custom field definitions. */
  System: "alpharun",
  /** User-defined custom field definitions. */
  User: "user"
};
const userCustomFieldTypes = [
  CustomFieldType.Text,
  CustomFieldType.Date,
  CustomFieldType.DateAndTime,
  CustomFieldType.Number,
  CustomFieldType.Boolean
];
function renderCustomFieldValueAsPlainText(field) {
  if (field.data.display_value) {
    return field.data.display_value;
  }
  if (field.data.value === null) {
    return "--";
  }
  switch (field.field_type) {
    case CustomFieldType.Boolean:
      return field.data.value ? "Yes" : "No";
    default:
      return String(field.data.value);
  }
}
function checkIsValidFieldName(fieldName) {
  return /^[a-z0-9_]+$/.test(fieldName);
}
function buildFieldNameForDisplayName(displayName) {
  return displayName.toLowerCase().replace(/'/g, "").replace(/[^a-z0-9]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
}
function checkIsInterviewDataExtractionCustomFieldName(fieldName) {
  return fieldName !== void 0 && fieldName.startsWith(`${interviewDataExtractionCustomFieldNamePrefix}:`);
}
const CustomFieldResourceType = {
  PROJECT: "project",
  CONTACT: "contact",
  ACCOUNT: "account",
  INTERVIEW: "interview",
  GRADING: "grading"
};
const allCustomFieldResourceTypes = [
  CustomFieldResourceType.PROJECT,
  CustomFieldResourceType.CONTACT,
  CustomFieldResourceType.ACCOUNT,
  CustomFieldResourceType.INTERVIEW,
  CustomFieldResourceType.GRADING
];
function checkIsCustomFieldResourceType(resourceType) {
  return allCustomFieldResourceTypes.some((type) => type === resourceType);
}
const customFieldResourceTypeLabels = {
  [CustomFieldResourceType.ACCOUNT]: "Account",
  [CustomFieldResourceType.CONTACT]: "Contact",
  [CustomFieldResourceType.INTERVIEW]: "Interview",
  [CustomFieldResourceType.PROJECT]: "Project",
  [CustomFieldResourceType.GRADING]: "Customer interaction"
};
const customFieldDefinitionSettingsDefaults = {
  description: null
};
const CustomFieldDefinitionImportanceSetting = {
  MustHave: "must_have"
};
function checkIsCustomFieldDefinitionImportanceMustHave(importance) {
  return importance === CustomFieldDefinitionImportanceSetting.MustHave;
}
const CustomFieldDefinitionImportanceOrder = {
  [CustomFieldDefinitionImportanceSetting.MustHave]: 1
};
function getCustomFieldDefinitionImportanceOrder(importance) {
  if (!importance) {
    return 999;
  }
  return CustomFieldDefinitionImportanceOrder[importance] ?? 999;
}
const dataExtractionCustomFieldResourceTypeSchema = z.enum([
  CustomFieldResourceType.INTERVIEW,
  CustomFieldResourceType.GRADING
]);
const customFieldDefinitionDescriptionSchema = z.string().trim().max(5e3);
const dataExtractionFieldDefinitionPostPayloadSchema = z.object({
  display_name: z.string(),
  field_type: customFieldTypeSchema,
  field_name: z.string().refine(checkIsValidFieldName),
  // We only support interview and grading for data extraction fields
  resource_type: dataExtractionCustomFieldResourceTypeSchema,
  description: customFieldDefinitionDescriptionSchema.nullish()
});
function isDataExtractionCustomFieldResourceType(resource_type) {
  return resource_type === CustomFieldResourceType.INTERVIEW || resource_type === CustomFieldResourceType.GRADING;
}
const interviewDataExtractionCustomFieldNamePrefix = "interview_data_extraction";
const gradingDataExtractionCustomFieldNamePrefix = "grading_data_extraction";
function getDataExtractionCustomFieldNamePrefix(resource_type) {
  switch (resource_type) {
    case CustomFieldResourceType.INTERVIEW:
      return interviewDataExtractionCustomFieldNamePrefix;
    case CustomFieldResourceType.GRADING:
      return gradingDataExtractionCustomFieldNamePrefix;
    default:
      assertUnreachable(resource_type);
  }
}
function buildDataExtractionFieldNameWithPrefix(resourceType, fieldName) {
  const prefix = getDataExtractionCustomFieldNamePrefix(resourceType);
  return `${prefix}:${fieldName}`;
}
function removeDataExtractionFieldNamePrefix(definition) {
  if (!isDataExtractionCustomFieldResourceType(definition.resource_type)) {
    return definition.field_name;
  }
  const prefix = getDataExtractionCustomFieldNamePrefix(
    definition.resource_type
  );
  return definition.field_name.replace(`${prefix}:`, "");
}
function isDataExtractionCustomField(customField) {
  if (customField.field_name.startsWith(
    interviewDataExtractionCustomFieldNamePrefix
  ) || customField.field_name.startsWith(
    gradingDataExtractionCustomFieldNamePrefix
  )) {
    return true;
  }
  return false;
}
export {
  CustomFieldDefinitionImportanceOrder,
  CustomFieldDefinitionImportanceSetting,
  CustomFieldProvider,
  CustomFieldResourceType,
  CustomFieldType,
  SpecialInterviewCustomFieldName,
  buildDataExtractionFieldNameWithPrefix,
  buildFieldNameForDisplayName,
  buildInterviewSectionAssessmentCustomFieldName,
  checkIsCustomFieldDefinitionImportanceMustHave,
  checkIsCustomFieldResourceType,
  checkIsInterviewDataExtractionCustomFieldName,
  checkIsInterviewSectionAssessmentCustomFieldName,
  checkIsValidFieldName,
  customFieldDataSchemaByType,
  customFieldDefinitionDescriptionSchema,
  customFieldDefinitionSettingsDefaults,
  customFieldResourceTypeLabels,
  customFieldTypeSchema,
  customFieldValueDataSchema,
  customFieldValueSchemaByType,
  dataExtractionCustomFieldResourceTypeSchema,
  dataExtractionFieldDefinitionPostPayloadSchema,
  dateCustomFieldTypeSchema,
  dateCustomFieldTypes,
  findSpecialCustomFieldDetails,
  getCustomFieldDefinitionImportanceOrder,
  getCustomFieldDisplayName,
  getDataExtractionCustomFieldNamePrefix,
  gradingDataExtractionCustomFieldNamePrefix,
  incomingCustomFieldSchema,
  interviewDataExtractionCustomFieldNamePrefix,
  interviewSectionAssessmentCustomFieldNamePrefix,
  isDataExtractionCustomField,
  removeDataExtractionFieldNamePrefix,
  renderCustomFieldValueAsPlainText,
  specialCustomFieldDetails,
  userCustomFieldTypes
};
//# sourceMappingURL=customField.js.map
